import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import type {
  LabelProviderSetup,
  SaveLabelProviderSetup,
} from "@/api/chitchats-supply";
import { useGetCarrierServices } from "@/api/carrier-services";
import type { SettlementTrigger } from "@/api/self-rated-supply";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
type Props = { carrierId: string; carrierName: string };
function message(error: unknown) {
  return axios.isAxiosError<{ error?: string }>(error)
    ? (error.response?.data.error ?? "Unable to save label provider setup.")
    : "Unable to save label provider setup.";
}
export function ProviderLabelSetupForm({
  carrierId,
  carrierName,
  setup,
  provider,
  providerName,
  saveSetup,
  isSaving,
  reload,
}: Props & {
  setup: LabelProviderSetup;
  provider: NonNullable<LabelProviderSetup["provider"]>;
  providerName: string;
  saveSetup: (input: SaveLabelProviderSetup) => Promise<unknown>;
  isSaving: boolean;
  reload: () => void;
}) {
  const { supply } = setup;
  const services = useGetCarrierServices({ carrierId });
  const [active, setActive] = useState(supply?.active ?? false);
  const [priority, setPriority] = useState(String(supply?.priority ?? 100));
  const [currency, setCurrency] = useState(supply?.terms?.currency ?? "");
  const [trigger, setTrigger] = useState<SettlementTrigger | "">(
    supply?.terms?.settlementTrigger ?? ""
  );
  const [serviceIds, setServiceIds] = useState(supply?.serviceIds ?? []);
  const [error, setError] = useState<string | null>(null);
  const incompatible =
    provider.kind !== "AGGREGATOR" ||
    (supply?.terms &&
      (supply.terms.payeeType !== "API_PROVIDER" ||
        supply.terms.payeeId !== provider.id ||
        supply.terms.payerType !== "EZX"));
  async function save() {
    const numericPriority = Number(priority);
    const needsTerms = active || !!supply?.terms || !!currency || !!trigger;
    if (needsTerms && (!trigger || !/^[A-Z]{3}$/.test(currency))) {
      setError("Choose payment timing and a three-letter currency code.");
      return;
    }
    if (
      !Number.isInteger(numericPriority) ||
      numericPriority <= 0 ||
      numericPriority > 2147483647
    ) {
      setError(
        "Enter a positive whole-number purchase priority, up to 2147483647."
      );
      return;
    }
    if (active && (!provider.active || !provider.account.configured)) {
      setError(
        `Activate ${providerName} and complete EZX’s account credentials in API Providers before enabling label purchases.`
      );
      return;
    }
    setError(null);
    try {
      await saveSetup({
        apiProviderId: provider.id,
        credentialId: null,
        expectedUpdatedAt: supply?.updatedAt ?? null,
        expectedTermsId: supply?.terms?.id ?? null,
        active,
        priority: numericPriority,
        serviceIds,
        terms:
          trigger && currency
            ? {
                payeeType: "API_PROVIDER",
                payeeId: provider.id,
                payerType: "EZX",
                settlementTrigger: trigger,
                currency,
              }
            : null,
      });
      toast.success(
        "Label provider setup saved. This does not verify provider access or complete shipping-service setup."
      );
    } catch (cause) {
      setError(message(cause));
    }
  }
  return (
    <div className="space-y-3">
      <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        <dt>Shipping carrier</dt>
        <dd>{carrierName}</dd>
        <dt>Label provider</dt>
        <dd>{providerName}</dd>
        <dt>Account used for labels</dt>
        <dd>EZX’s {providerName} account</dd>
        <dt>Who pays</dt>
        <dd>EZX</dd>
        <dt>Who receives payment</dt>
        <dd>{providerName}</dd>
      </dl>
      <p className="text-sm text-muted-foreground">
        Account credentials:{" "}
        {provider.account.configured
          ? "required fields are filled in"
          : "incomplete"}
        . Provider: {provider.active ? "active" : "inactive"}. This does not
        verify access to {providerName}.{" "}
        <a href="/carriers" className="underline">
          Manage integration account (API Providers)
        </a>
      </p>
      {incompatible ? (
        <p role="alert">
          This saved provider classification or payment arrangement cannot be
          changed with this editor. Existing settings have been preserved.
        </p>
      ) : (
        <fieldset disabled={isSaving} className="space-y-3">
          <p className="text-sm">
            {supply
              ? supply.active
                ? "Saved label-purchase setup is enabled."
                : "Saved label-purchase setup is disabled."
              : "No label-purchase setup saved."}{" "}
            Shipping services and successful quote/label checks are still
            required.
          </p>
          <Label
            htmlFor={`provider-${provider.id}-active`}
            className="flex items-center gap-2"
          >
            <Checkbox
              id={`provider-${provider.id}-active`}
              checked={active}
              onCheckedChange={(checked) => setActive(checked === true)}
              disabled={isSaving}
            />
            Enable label purchases through {providerName}
          </Label>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor={`provider-${provider.id}-trigger`}>
                Payment timing
              </Label>
              <Select
                value={trigger}
                disabled={isSaving}
                onValueChange={(value) => {
                  if (value === "unset") {
                    setTrigger("");
                    return;
                  }
                  if (
                    value === "" ||
                    value === "AT_PURCHASE" ||
                    value === "ON_COMPLETION" ||
                    value === "ON_INVOICE"
                  )
                    setTrigger(value);
                }}
              >
                <SelectTrigger
                  id={`provider-${provider.id}-trigger`}
                  className="w-full"
                >
                  <SelectValue placeholder="Choose payment timing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">Clear payment timing</SelectItem>
                  <SelectItem value="AT_PURCHASE">At label purchase</SelectItem>
                  <SelectItem value="ON_COMPLETION">On completion</SelectItem>
                  <SelectItem value="ON_INVOICE">On invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor={`provider-${provider.id}-currency`}>
                Currency
              </Label>
              <Input
                id={`provider-${provider.id}-currency`}
                placeholder="e.g. CAD"
                value={currency}
                maxLength={3}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              />
            </div>
            <div>
              <Label htmlFor={`provider-${provider.id}-priority`}>
                Purchase priority
              </Label>
              <Input
                id={`provider-${provider.id}-priority`}
                type="number"
                min={1}
                max={2147483647}
                step={1}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                A higher number is preferred if another purchasing option is
                enabled for this shipping carrier.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Shipping services allowed</h4>
            <p className="text-xs text-muted-foreground">
              Leave all unchecked to allow all shipping services for this
              carrier. {providerName} must offer the services you use.
            </p>
            {services.isPending ? (
              <p>Loading shipping services…</p>
            ) : services.isError ? (
              <p role="alert">
                Could not load shipping services. Saved restrictions are
                preserved.{" "}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void services.refetch()}
                >
                  Retry services
                </Button>
              </p>
            ) : (
              <>
                {services.data?.map((service) => (
                  <Label
                    key={service.id}
                    htmlFor={`provider-${provider.id}-service-${service.id}`}
                    className="flex items-center gap-2"
                  >
                    <Checkbox
                      id={`provider-${provider.id}-service-${service.id}`}
                      disabled={isSaving}
                      checked={serviceIds.includes(service.id)}
                      onCheckedChange={(checked) =>
                        setServiceIds(
                          checked === true
                            ? [...serviceIds, service.id]
                            : serviceIds.filter((id) => id !== service.id)
                        )
                      }
                    />
                    {service.name} ({service.code})
                  </Label>
                ))}
                {serviceIds
                  .filter(
                    (id) => !services.data?.some((service) => service.id === id)
                  )
                  .map((id) => (
                    <Label
                      key={id}
                      htmlFor={`provider-${provider.id}-service-${id}`}
                      className="flex items-center gap-2"
                    >
                      <Checkbox
                        id={`provider-${provider.id}-service-${id}`}
                        disabled={isSaving}
                        checked
                        onCheckedChange={() =>
                          setServiceIds(
                            serviceIds.filter((selected) => selected !== id)
                          )
                        }
                      />
                      Saved service unavailable ({id})
                    </Label>
                  ))}
                {!services.data?.length && (
                  <p className="text-sm">
                    No shipping services yet. Add them in this carrier’s
                    Services section.
                  </p>
                )}
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Changed payment terms apply to future quotes. Existing quotes and
            shipments keep their recorded terms.
          </p>
          {error && (
            <div role="alert">
              <p>{error}</p>
              <Button type="button" variant="outline" onClick={reload}>
                Reload saved label provider setup
              </Button>
            </div>
          )}
          <Button type="button" onClick={() => void save()}>
            {isSaving
              ? "Saving label provider setup…"
              : "Save label provider setup"}
          </Button>
        </fieldset>
      )}
    </div>
  );
}
